import { useCallback, useMemo } from 'react';
import { useExperiments } from 'src/services/experiments/ContextProviders/ExperimentProvider';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';
import { areIntegrationTestsRunning } from 'src/utils/validators/areIntegrationTestsRunning';
import { isServerSide } from 'src/utils/validators/isServerSide';
import { getParsedSessionStorageItem } from 'src/services/storage/session';
import { SessionStorageKeys } from 'src/services/storage/constants';
import { useSessionManagement } from './useSessionManagement';
import { useZillowSession } from './useZillowSession';

const MFA_CALLBACK_PARAM = 'mfa_completed';

/**
 * Hook for managing step-up MFA verification for sensitive dashboard actions.
 *
 * Flow:
 * 1. User on dashboard clicks sensitive CTA (e.g., Blend redirect)
 * 2. If MFA is disabled (feature flag OFF or tests), access is granted immediately
 * 3. If MFA is enabled and user hasn't verified, hook triggers MFA step-up authentication
 * 4. After MFA, user is redirected back with ?mfa_completed=true
 * 5. Hook detects the parameter and cleans up the URL
 * 6. Subsequent actions check session.mfaVerified from server-side session data
 *
 * Cypress/Test Override:
 * - Automatically detects Cypress tests via `areIntegrationTestsRunning()`
 * - Bypasses all MFA checks during test execution
 * - No manual override or configuration needed
 *
 * @returns Object containing:
 * - hasMfaAccess: Whether user has completed MFA and can access sensitive content
 * - requestMfaAccess: Function to trigger MFA step-up authentication
 * - clearMfaAccess: Function to clear MFA verification (triggers re-authentication on next request)
 */
export const useMfaVerification = () => {
    const { startSession } = useSessionManagement();
    const { experiments } = useExperiments();
    const { session } = useZillowSession();

    // Determine if MFA is required - only check client-side to avoid SSR issues
    const mfaRequired = useMemo(() => {
        // During SSR, default to MFA not required (will be recalculated client-side)
        if (isServerSide()) {
            return false;
        }

        // Disable MFA for Cypress/integration tests
        if (areIntegrationTestsRunning()) {
            return false;
        }

        // MFA is enabled by default for security — the flag acts as a kill switch (OFF disables MFA)
        const mfaFeatureFlag = experiments[ExperimentService.Domain.ActiveExperiment.ZHL_HOMELOANS_DASHBOARD_MFA_FF];
        return mfaFeatureFlag?.treatment !== ExperimentService.Domain.FeatureFlagStatus.OFF;
    }, [experiments]);

    // Page-level MFA (withAuth) sets this flag before redirecting to Auth0.
    // If it's set, the user already went through the MFA flow at the page level —
    // don't trigger a redundant CTA-level redirect. Server-side API enforcement
    // remains the authoritative security boundary.
    const pageLevelMfaAttempted =
        !isServerSide() && getParsedSessionStorageItem<boolean>(SessionStorageKeys.MFA_STEP_UP_ATTEMPTED) === true;

    const hasMfaAccess = !mfaRequired || (session?.mfaVerified ?? false) || pageLevelMfaAttempted;

    /**
     * Triggers step-up MFA authentication (or grants access if MFA is disabled).
     * Adds callback parameter to redirect URL to detect successful MFA completion.
     */
    const requestMfaAccess = useCallback(() => {
        // If MFA is not required, user already has access via feature flag
        if (!mfaRequired) {
            return;
        }

        // If user already has MFA access (verified server-side), don't trigger again
        if (hasMfaAccess) {
            return;
        }

        // Add MFA callback parameter to current URL for redirect
        const currentUrl = new URL(globalThis.location.href);
        currentUrl.searchParams.set(MFA_CALLBACK_PARAM, 'true');

        startSession('Dashboard CTA step-up MFA', {
            mfaRequired: true,
            callbackUrl: currentUrl.toString(),
        });
    }, [startSession, mfaRequired, hasMfaAccess]);

    /**
     * Clears the MFA verification state by forcing re-authentication.
     * This will trigger a new MFA flow on the next access request.
     * Note: This requires the user to sign in again with MFA to restore access.
     */
    const clearMfaAccess = useCallback(() => {
        // To clear MFA access, we need to trigger re-authentication
        // This will create a new session without the MFA scope
        startSession('Clear MFA access - re-authentication required', {
            mfaRequired: false,
        });
    }, [startSession]);

    return {
        hasMfaAccess,
        requestMfaAccess,
        clearMfaAccess,
    };
};
